import { Button } from '@acpaas-ui/react-components';
import { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { fetchReset } from '../../actions/fetch.actions';
import { INPUT_STATES } from '../../components/forms/forms.utils';
import TextArea from '../../components/forms/TextArea';
import TextField from '../../components/forms/TextField';
import { MODALS } from '../../constants/modals.constants';
import formValidators from '../../form-validators';
import { useModal } from '../../hooks/useModal.hook';
import { useResourceInput } from '../../hooks/useResourceInput';
import { ADMIN_CONTACTS_RESOURCE } from '../../resources/admin/admin.contacts.resource';
import { ADMIN_PARTNERS_RESOURCE } from '../../resources/admin/admin.partners.resource';
import partnersService from '../../services/api/partners.service';
import DeleteWarningModal from '../DeleteWarningModal';
import Modal from '../Modal';
import ModalTitle from '../ModalTitle';

const UpdatePartnerModal = ({ isOpen, onClose, form }) => {
  const dispatch = useDispatch();
  const modalContentRef = useRef(null);
  const [shouldResetOnClose, setShouldResetOnClose] = useState(false);
  const [originalName] = useState(form?.data?.name ?? '');
  const { data, input, isSubmitting, submitStart, submitOk, submitError, error, reset } = form;
  const resource = {
    isFetching: false,
    isSubmitting,
    getValue: (field) => data?.[field],
    setValue: (value, field, options = {}) => {
      submitStart();

      partnersService.update(
        data.id,
        { [field]: value },
        {
          onSuccess: () => {
            submitOk();
            input(value, field);
            setShouldResetOnClose(true);
            options.onSuccess();
          },
          onError: (message) => {
            submitError();
            options.onError(message);
          }
        }
      );
    },
    validators: {
      email: formValidators.emailNonRequired,
      phone: formValidators.phoneSimple
    },
    onCancel: reset
  };
  const deleteModal = useModal(MODALS.PARTNER_DELETE_WARNING_MODAL);

  const handleClose = () => {
    if (shouldResetOnClose) {
      dispatch(fetchReset(ADMIN_PARTNERS_RESOURCE));
    }
    setShouldResetOnClose(false);
    onClose();
  };

  const handleDelete = (onError, onSuccess) => {
    partnersService.remove(data.id, {
      onError,
      onSuccess: () => {
        onSuccess();
        dispatch(fetchReset(ADMIN_PARTNERS_RESOURCE));
        dispatch(fetchReset(ADMIN_CONTACTS_RESOURCE));
        handleClose();
      }
    });
  };

  const nameInput = useResourceInput('name', resource);

  return (
    <>
      <Modal
        ref={modalContentRef}
        className="spo-o-update-partner-modal"
        title={<ModalTitle hasError={!!error} title="Partner aanpassen" />}
        isOpen={isOpen}
        onClose={handleClose}
        disableConfirm
        disableCancel
        isSubmitting={isSubmitting}
        extraFooterActions={
          <Button type="danger" outline disables={isSubmitting} onClick={deleteModal.onOpen}>
            Verwijderen
          </Button>
        }
      >
        <TextField
          label="Naam"
          {...nameInput}
          onSubmit={
            nameInput.state === INPUT_STATES.EDIT && originalName === nameInput.value ? undefined : nameInput.onSubmit
          }
        />
        <TextField label="Postcode" {...useResourceInput('postcode', resource)} />
        <TextField label="Gemeente" {...useResourceInput('city', resource)} />
        <TextField label="Straatnaam" {...useResourceInput('streetName', resource)} />
        <TextField label="Huisnummer" {...useResourceInput('houseNumber', resource)} />
        <TextField label="Provincie" {...useResourceInput('province', resource)} />
        <TextField label="Telefoon" {...useResourceInput('phone', resource)} />
        <TextField label="E-mail" {...useResourceInput('email', resource)} />
        <TextArea
          className="u-margin-top u-margin-bottom-xs"
          label="Opmerkingen"
          {...useResourceInput('remarks', resource)}
        />
        {!!error && <p className="u-text-danger">{error}</p>}
      </Modal>
      <DeleteWarningModal
        {...deleteModal}
        warningText="Ben je zeker dat je deze partner wilt verwijderen?"
        onConfirm={handleDelete}
      />
    </>
  );
};

export default UpdatePartnerModal;
